import React, { useContext, useEffect } from 'react';
import c from 'classnames';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { FormPrice, Modal } from '../../components';
import {
  MAX_CV_INSTALLATION_COST,
  MAX_CV_MAINTENANCE_COST,
  MAX_GAS_FIXED_COST,
  MAX_GAS_GRID_MANAGEMENT_COST,
  MIN_CV_INSTALLATION_COST,
  MIN_CV_MAINTENANCE_COST,
  MIN_GAS_FIXED_COST,
  MIN_GAS_GRID_MANAGEMENT_COST,
} from '../../config/constants';
import DataContext from '../../contexts/DataContext';
import ModalContext from '../../contexts/ModalContext';

import './style.scss';
import gtm from '../../../../shared/helpers/gtm';
import Button from '../../../../shared/components/Button';

interface Props {
  className?: string;
}

const GasCalculator: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    gasGridManagementCost,
    gasFixedCost,
    cvInstallationCost,
    cvMaintenanceCost,
    updateGasGridManagementCost,
    updateGasFixedCost,
    updateCvInstallationCost,
    updateCvMaintenanceCost,
  } = useContext(DataContext);

  const { modalOpen, updateModalOpen } = useContext(ModalContext);

  const classNames = c(
    'GasCalculator',
    className,
  );

  useEffect(() => {
    history.push(history.location);

    const unlisten = history.listen((location, action) => {
      if (action === 'POP' && modalOpen) {
        updateModalOpen(false);
        return;
      }

      history.goBack();
    });

    return () => {
      unlisten();
    };
  }, [modalOpen]);

  return (
    <Modal className={classNames} closeButtonText={t('heat_calculator.buttons.cancel')}>
      <p>{t('heat_calculator.paragraphs.gas_calculator_intro')}</p>

      <Formik
        initialValues={{
          gasGridManagementCost,
          gasFixedCost,
          cvInstallationCost,
          cvMaintenanceCost,
        }}
        onSubmit={(values) => {
          updateModalOpen(false);
          updateGasGridManagementCost(Number(values.gasGridManagementCost));
          updateGasFixedCost(Number(values.gasFixedCost));
          updateCvInstallationCost(Number(values.cvInstallationCost));
          updateCvMaintenanceCost(Number(values.cvMaintenanceCost));

          // GTM.
          gtm.legacyTrackUserEvent('submitGasValues', {
            gas_grid_management_cost: values.gasGridManagementCost,
            gas_fixed_cost: values.gasFixedCost,
            cv_installation_cost: values.cvInstallationCost,
            cv_maintenance_cost: values.cvMaintenanceCost,
          });
        }}
      >
        {({ isSubmitting, setSubmitting }) => (
          <Form>
            <FormPrice
              name="gasGridManagementCost"
              label={t('heat_calculator.form.gas_grid_management_cost.label')}
              min={MIN_GAS_GRID_MANAGEMENT_COST}
              max={MAX_GAS_GRID_MANAGEMENT_COST}
              required
              disabled={isSubmitting}
            />
            <FormPrice
              name="gasFixedCost"
              label={t('heat_calculator.form.gas_fixed_cost.label')}
              min={MIN_GAS_FIXED_COST}
              max={MAX_GAS_FIXED_COST}
              required
              disabled={isSubmitting}
            />
            <FormPrice
              name="cvInstallationCost"
              label={t('heat_calculator.form.cv_installation_cost.label')}
              min={MIN_CV_INSTALLATION_COST}
              max={MAX_CV_INSTALLATION_COST}
              required
              disabled={isSubmitting}
            />
            <FormPrice
              name="cvMaintenanceCost"
              label={t('heat_calculator.form.cv_maintenance_cost.label')}
              min={MIN_CV_MAINTENANCE_COST}
              max={MAX_CV_MAINTENANCE_COST}
              required
              disabled={isSubmitting}
            />
            <Button
              type="primary"
              htmlType="submit"
              variation="center"
              onClick={() => {
                setSubmitting(true);
              }}
            >
              {t('heat_calculator.buttons.restart_calculation')}
            </Button>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default GasCalculator;
